import { useT } from "@/i18n";

// Plutchik-inspired 3-tier emotion wheel. Tree keys are stable and stored as
// the answer value; labels resolve through i18n (emo_* keys ported from mobile).
const TREE = {
  joy: { color: "#F0C674", secondary: {
    content: ["peaceful", "grateful", "warm"],
    excited: ["eager", "energized", "thrilled"],
    proud: ["confident", "seen", "accomplished"],
    loving: ["tender", "connected", "cherished"],
  } },
  sadness: { color: "#7A9EBF", secondary: {
    lonely: ["isolated", "abandoned", "unseen"],
    hurt: ["heartbroken", "disappointed", "betrayed"],
    hopeless: ["defeated", "empty", "lost"],
    grief: ["aching", "tearful", "mourning"],
  } },
  fear: { color: "#B196C4", secondary: {
    anxious: ["restless", "worried", "overwhelmed"],
    scared: ["terrified", "threatened", "frozen"],
    insecure: ["inadequate", "exposed", "small"],
    helpless: ["powerless", "trapped", "paralyzed"],
  } },
  anger: { color: "#D48A8A", secondary: {
    irritated: ["annoyed", "impatient", "frustrated"],
    hostile: ["resentful", "bitter", "jealous"],
    furious: ["enraged", "hateful", "violent"],
    hurt: ["betrayed", "disrespected", "unfair"],
  } },
  disgust: { color: "#9AB08A", secondary: {
    revolted: ["nauseated", "repulsed"],
    judgmental: ["critical", "contemptuous", "superior"],
    ashamed: ["embarrassed", "guilty", "unworthy"],
  } },
  surprise: { color: "#E7B69F", secondary: {
    startled: ["jolted", "shocked"],
    curious: ["intrigued", "amazed", "open"],
    confused: ["disoriented", "perplexed"],
  } },
};

function Chip({ label, active, color, bg, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="rounded-full border-[1.5px] px-3 py-1.5 text-sm transition-colors"
      style={{ borderColor: color, backgroundColor: active ? bg : "transparent", color: active ? color : "inherit", fontWeight: active ? 700 : 400 }}
    >
      {label}
    </button>
  );
}

export function EmotionWheel({ value, onChange }) {
  const t = useT();
  const primary = value?.primary;
  const secondary = value?.secondary;
  const tertiary = value?.tertiary;

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("emo_primary")}</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(TREE).map(([k, v]) => (
            <Chip key={k} testId={`ew-primary-${k}`} label={t(`emo_${k}`)} active={primary === k} color={v.color} bg={v.color}
              onClick={() => onChange({ primary: k, secondary: null, tertiary: null })} />
          ))}
        </div>
      </div>

      {primary ? (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("emo_secondary")}</p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(TREE[primary].secondary).map((k) => (
              <Chip key={k} testId={`ew-secondary-${k}`} label={t(`emo_${k}`)} active={secondary === k} color={TREE[primary].color} bg={TREE[primary].color + "33"}
                onClick={() => onChange({ primary, secondary: k, tertiary: null })} />
            ))}
          </div>
        </div>
      ) : null}

      {primary && secondary ? (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("emo_specific")}</p>
          <div className="flex flex-wrap gap-2">
            {(TREE[primary].secondary[secondary] || []).map((k) => (
              <Chip key={k} testId={`ew-tertiary-${k}`} label={t(`emo_${k}`)} active={tertiary === k} color={TREE[primary].color} bg={TREE[primary].color + "55"}
                onClick={() => onChange({ primary, secondary, tertiary: k })} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
